import Button from '@/common/components/base/button/Button';
import { convertToEnglishDigits } from '@/common/components/base/input/base/changeHandlers/convertToEnglishDigits';
import Input from '@/common/components/base/input/base/Input';
import { CalendarModalType } from '@/common/components/base/input/dateInput/modals/CalendarModal.type';
import MainModal from '@/common/components/base/modal/Modal';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import Typography from '@/common/components/base/typography/Typography';
import React, { useRef, useState } from 'react';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { Calendar, DateObject } from 'react-multi-date-picker';
import { css } from 'styled-system/css';
import { Box } from 'styled-system/jsx';
import { token } from 'styled-system/tokens';
const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const CalendarModal = ({
  field,
  onChange,
  buttonText,
  modalLabel,
  open,
  closeModal,
  persianDate,
  calendarInputs,
  setCalendarInputs,
  date,
  setDate,
  minDate,
  maxDate,
}: CalendarModalType) => {
  const [error, setError] = useState<string>('');
  const updatedDate = persianDate.set('date', date);
  const yearRef: React.Ref<HTMLInputElement> | null = useRef(null);
  const monthRef: React.Ref<HTMLInputElement> | null = useRef(null);
  const dayRef: React.Ref<HTMLInputElement> | null = useRef(null);
  const inputOnChange = (date: DateObject) => {
    minDate?.setHours(1, 0, 0, 0);
    maxDate?.setHours(1, 0, 0, 0);

    const selectedDate = new Date(date.toDate());

    if (
      (minDate && selectedDate < minDate) ||
      (maxDate && selectedDate > maxDate)
    ) {
      setError('تاریخ خارج از بازه مجاز انتخاب شده است');
    } else {
      setError('');
      field.onChange(`${date.year}/${date.month}/${date.day}`);
      onChange?.(`${date.year}/${date.month}/${date.day}`);
      setDate(date);
    }
  };

  const handleManualInputChange = (
    value: string,
    inputType: 'dayInput' | 'monthInput' | 'yearInput',
  ) => {
    const newValue = value.trim();
    const numericValue = Number(newValue);
    const length = newValue.length;
    const requiredLength =
      inputType === 'dayInput' || inputType === 'monthInput' ? 2 : 4;
    const requiredPeriod =
      inputType === 'dayInput'
        ? date?.month?.length
        : inputType === 'monthInput'
          ? 12
          : undefined;

    if (newValue === '' && !numericValue) {
      setCalendarInputs((prev) => ({
        ...prev,
        [inputType]: newValue,
      }));
      return null;
    }

    const convertedValue = convertToEnglishDigits(newValue);
    if (isNaN(+convertedValue)) {
      return;
    }

    if (
      length > requiredLength ||
      (requiredPeriod !== undefined && +convertedValue > requiredPeriod)
    ) {
      return;
    }

    setCalendarInputs((prev) => ({
      ...prev,
      [inputType]: newValue,
    }));

    updatedDate.set(
      inputType === 'dayInput'
        ? 'day'
        : inputType === 'monthInput'
          ? 'month'
          : 'year',
      +convertedValue,
    );
    setDate(updatedDate);
    inputOnChange(updatedDate);
  };

  const handleManualMonthChange = (date: DateObject) => {
    setCalendarInputs((prev) => ({
      ...prev,
      monthInput: date.month.number,
    }));
    inputOnChange(date);
  };

  const handleManualYearChange = (date: DateObject) => {
    setCalendarInputs((prev) => ({
      ...prev,
      yearInput: date.year,
    }));
    inputOnChange(date);
  };

  const handleCloseModal = () => {
    const resetDate = new DateObject({ calendar: persian, locale: persian_fa });
    closeModal();
    if (field.value === '') {
      setDate(null);
      setCalendarInputs({
        dayInput: resetDate.day,
        monthInput: resetDate.month.number,
        yearInput: resetDate.year,
      });
    }
  };

  const onSubmit = () => {
    closeModal();
    date ? inputOnChange(date) : null;
  };
  return (
    <MainModal open={open} closeModal={handleCloseModal}>
      <Box mb="10px">
        <TitleDescription mb="20px">{modalLabel}</TitleDescription>
        <Calendar
          className="calendar"
          headerOrder={['LEFT_BUTTON', 'RIGHT_BUTTON', 'MONTH_YEAR']}
          calendar={persian}
          locale={persian_fa}
          weekDays={weekDays}
          value={field.value || date}
          minDate={minDate}
          maxDate={maxDate}
          mapDays={({
            date,
            currentMonth,
            isSameDate,
            today,
            selectedDate,
          }) => {
            const isCurrentMonth = date.month.index === currentMonth.index;
            const isBeforeMinDate = minDate && date < new DateObject(minDate);
            const isAfterMaxDate = maxDate && date > new DateObject(maxDate);

            if (isBeforeMinDate || isAfterMaxDate) {
              return {
                disabled: true,
                style: {
                  color: token('colors.black-4'),
                  pointerEvents: 'none',
                  opacity: 0.4,
                },
              };
            }

            const isWeekend = [0, 6].includes(date.weekDay.index - 6);
            if (isWeekend && isSameDate(date, selectedDate)) {
              return {
                style: { backgroundColor: token('colors.purple-primary') },
              };
            }
            if (isWeekend && isCurrentMonth) {
              return {
                style: { color: token('colors.red') },
              };
            }
            if (isSameDate(date, selectedDate)) {
              return {
                style: {
                  ...classes.day,
                  backgroundColor: token('colors.purple-primary'),
                  color: 'white',
                },
              };
            }
            if (isSameDate(date, today) && !isSameDate(date, selectedDate)) {
              return {
                style: {
                  ...classes.day,
                  borderBottom: `2px solid ${token('colors.purple-3')}`,
                  backgroundColor: 'transparent',
                  borderRadius: '0px',
                  color: token('colors.black-2'),
                },
              };
            }
          }}
          onFocusedDateChange={(date) => {
            setCalendarInputs((prev) => ({
              ...prev,
              dayInput: date?.day ?? '',
              monthInput: date?.month.number ?? '',
              yearInput: date?.year ?? '',
            }));
            date && inputOnChange(date);
            date ? setDate(date) : null;
          }}
          monthYearSeparator=" "
          showOtherDays
          onMonthChange={(date) => handleManualMonthChange(date)}
          onYearChange={(date) => handleManualYearChange(date)}
        />
        <Box
          shadow="cardShadow"
          borderRadius="8px"
          bg="white"
          display="flex"
          alignItems="center"
          gap="12"
          px="30px"
          py="10px"
          mt="6px"
          backgroundColor="white"
          textStyle="h1"
        >
          <Input
            inputProps={{
              onClick: () => {
                const input = dayRef.current;
                if (input) {
                  input.select();
                }
              },
              value: calendarInputs.dayInput,
              inputMode: 'numeric',
              className: css({
                h: '38px',
                minH: 'initial',
                textAlign: 'center !important',
                borderColor: 'black-4 !important',
              }),
              onChange: (e) =>
                handleManualInputChange(e.target.value, 'dayInput'),
            }}
            inputRef={dayRef}
            hasErrorMassage={false}
            hasLabel={false}
          />
          /
          <Input
            inputProps={{
              onClick: () => {
                const input = monthRef.current;
                if (input) {
                  input.select();
                }
              },
              inputMode: 'numeric',
              value: calendarInputs.monthInput,
              className: css({
                h: '38px',
                minH: 'initial',
                textAlign: 'center !important',
                borderColor: 'black-4 !important',
              }),
              onChange: (e) =>
                handleManualInputChange(e.target.value, 'monthInput'),
            }}
            inputRef={monthRef}
            hasErrorMassage={false}
            hasLabel={false}
          />
          /
          <Input
            inputProps={{
              onClick: () => {
                const input = yearRef.current;
                if (input) {
                  input.select();
                }
              },
              inputMode: 'numeric',
              value: calendarInputs.yearInput,
              className: css({
                h: '38px',
                minH: 'initial',
                textAlign: 'center !important',
                borderColor: 'black-4 !important',
              }),
              onChange: (e) =>
                handleManualInputChange(e.target.value, 'yearInput'),
            }}
            inputRef={yearRef}
            hasErrorMassage={false}
            hasLabel={false}
          />
        </Box>
        <Box height={40} mt="10px">
          <Typography textStyle="b2" color="red">
            {error}
          </Typography>
        </Box>
        <Button fullWidth onClick={onSubmit} disabled={!date || !!error}>
          {buttonText}
        </Button>
      </Box>
    </MainModal>
  );
};

export default CalendarModal;

const classes = {
  day: {
    boxShadow: 'none',
  },
};
